import React, { useContext, useEffect, useState } from 'react';
import {
  Box, Typography, Button, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTheme } from '@mui/system';
import { StyledDataGrid, customLocaleText } from '../common/StyledDataGrid';
import axios from '../../config/axiosInstance';
import { UserContext } from '../context/UserContext';
import { useSnackbar } from 'notistack';
import useConfirm from '../calendar/dialogs/UseConfirm';

export default function TypeLicensePage() {
  const { permissions, user } = useContext(UserContext);
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, ConfirmDialog } = useConfirm();
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const canRead = permissions.includes('licenses.read');
  const canWrite = permissions.includes('licenses.write');
  const [typeLicenses, setTypeLicenses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingTypeLicense, setEditingTypeLicense] = useState(null);

  const fetchTypeLicenses = async () => {
    try {
      const res = await axios.get('/type_license');
      setTypeLicenses(res.data);
    } catch (error) {
      enqueueSnackbar('Error al listar tipos de licencia', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (canRead) {
      fetchTypeLicenses();
    }
  }, [canRead]);

  // Crear nuevo tipo de licencia
  const handleAdd = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es obligatorio.', { variant: 'warning' });
      return;
    }
    try {
      const params = new URLSearchParams({
        name,
        description
      });
      await axios.post(`/type_license?${params.toString()}`);
      setOpen(false);
      setName('');
      setDescription('');
      fetchTypeLicenses();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Error al crear tipo de licencia', { variant: 'error' });
    }
  };

  // Editar tipo de licencia
  const handleEdit = (typeLicense) => {
    setEditingTypeLicense(typeLicense);
    setName(typeLicense.name || "");
    setDescription(typeLicense.description || "");
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es obligatorio.', { variant: 'warning' });
      return;
    }
    try {
      const params = new URLSearchParams({
        id: editingTypeLicense.id,
        name,
        description,
        enabled: true
      });
      await axios.put(`/type_license/edit?${params.toString()}`);
      setEditOpen(false);
      setEditingTypeLicense(null);
      fetchTypeLicenses();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Error al editar tipo de licencia', { variant: 'error' });
    }
  };

  // Deshabilitar tipo de licencia
  const handleDisable = async (typeLicense) => {
    const ok = await confirm({
      title: 'Deshabilitar tipo de licencia',
      content: `¿Seguro que querés deshabilitar "${typeLicense.name}"?`,
      confirmText: 'Deshabilitar',
    });
    if (!ok) return;
    try {
      await axios.delete(`/type_license/delete?id=${typeLicense.id}`);
      enqueueSnackbar('Tipo de licencia deshabilitado correctamente.', { variant: 'success' });
      fetchTypeLicenses();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Error al deshabilitar tipo de licencia', { variant: 'error' });
    }
  };

  // Habilitar tipo de licencia (solo super_admin)
  const handleEnable = async (typeLicense) => {
    const ok = await confirm({
      title: 'Habilitar tipo de licencia',
      content: `¿Seguro que querés habilitar "${typeLicense.name}"?`,
      confirmText: 'Habilitar',
    });
    if (!ok) return;
    try {
      const params = new URLSearchParams({
        id: typeLicense.id,
        name: typeLicense.name,
        description: typeLicense.description || '',
        enabled: true
      });
      await axios.put(`/type_license/edit?${params.toString()}`);
      enqueueSnackbar('Tipo de licencia habilitado correctamente.', { variant: 'success' });
      fetchTypeLicenses();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Error al habilitar tipo de licencia', { variant: 'error' });
    }
  };

  const normalizedNameFilter = nameFilter.trim().toLowerCase();
  const filteredTypeLicenses = typeLicenses.filter((tl) => {
    if (normalizedNameFilter && !(tl.name || '').toLowerCase().includes(normalizedNameFilter)) return false;
    if (statusFilter === 'enabled') return tl.enabled !== false;
    if (statusFilter === 'disabled') return tl.enabled === false;
    return true;
  });

  const sortedTypeLicenses = [...filteredTypeLicenses].sort((a, b) => {
    if (a.enabled === b.enabled) return 0;
    if (a.enabled) return -1;
    return 1;
  });

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.4, editable: false },
    { field: 'name', headerName: 'Nombre', flex: 1, editable: false },
    { field: 'description', headerName: 'Descripción', flex: 1.5, editable: false },
    {
      field: 'enabled',
      headerName: 'Estado',
      flex: 0.6,
      editable: false,
      renderCell: (params) => (params.value
        ? <CheckCircleIcon sx={{ color: theme.palette.success.main, backgroundColor: '#E8F5E9', borderRadius: '50%', padding: '2px' }} titleAccess="Habilitado" />
        : <CancelIcon sx={{ color: theme.palette.error.main, backgroundColor: '#ffcdd2', borderRadius: '100%', padding: '2px' }} titleAccess="Deshabilitado" />),
    },
    ...(canWrite ? [{
      field: 'actions',
      headerName: 'Acciones',
      sortable: false,
      width: 140,
      renderCell: ({ row }) => (
        <>
          <IconButton color="primary" size="small" title="Editar" onClick={() => handleEdit(row)} disabled={row.enabled === false}>
            <EditIcon />
          </IconButton>
          <IconButton color="primary" size="small" title="Deshabilitar" onClick={() => handleDisable(row)} disabled={row.enabled === false}>
            <BlockIcon />
          </IconButton>
          {row.enabled === false && (
            <Tooltip title={isSuperAdmin ? 'Habilitar' : 'Solo un super administrador puede habilitar'}>
              <span>
                <IconButton color="primary" size="small" disabled={!isSuperAdmin} onClick={() => handleEnable(row)}>
                  <RestoreIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      ),
    }] : []),
  ];

  if (!canRead) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" color="error">No tienes permiso para ver esta sección.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: theme.palette.primary.dark, textAlign: 'center' }}>
        Tipos de Licencia
      </Typography>
      {canWrite && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setName('');
              setDescription('');
              setOpen(true);
            }}
          >
            Agregar tipo de licencia
          </Button>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 900 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Buscar por nombre"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="enabled">Habilitados</MenuItem>
                <MenuItem value="disabled">Deshabilitados</MenuItem>
              </Select>
            </FormControl>
            {(nameFilter || statusFilter !== 'all') && (
              <Button onClick={() => { setNameFilter(''); setStatusFilter('all'); }}>
                Limpiar filtros
              </Button>
            )}
          </Box>
          <StyledDataGrid
            rows={sortedTypeLicenses}
            columns={columns}
            getRowId={(row) => row.id}
            hideFooter
            localeText={customLocaleText}
            disableSelectionOnClick
            autoHeight
            disableColumnMenu
            disableColumnSelector
            getRowClassName={(params) => (params.row.enabled === false ? 'inactive-row' : '')}
          />
        </Box>
      </Box>

      {ConfirmDialog}

      {/* Modal para agregar */}
      <Dialog open={open && canWrite} onClose={() => setOpen(false)}>
        <DialogTitle>Nuevo tipo de licencia</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nombre"
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Descripción"
            fullWidth
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={handleAdd} variant="contained">Crear</Button>
        </DialogActions>
      </Dialog>

      {/* Modal para editar */}
      <Dialog open={editOpen && canWrite} onClose={() => setEditOpen(false)}>
        <DialogTitle>Editar tipo de licencia</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Nombre"
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Descripción"
            fullWidth
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
          <Button onClick={handleEditSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
